/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Map, List } from 'immutable';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { RECEIVE_BUSES } from 'constants/ActionTypes';
import { Icon, Spacing, Text, TicketBox, Badge, Button, Alert } from '@reservamos/elements';
import TripItem from 'components/search/TripItem';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  getSearchDateParamFromMoment,
  getMomentFromSearchDateParam,
} from 'utils/search/searchDateParam';
import SearchWidget from 'ui/atoms/SearchWidget';
import { search as searchApi } from 'js-api-client';
import { camelizeKeys } from 'humps';
import { setCities, setTerminals, setLines } from '@/actions/catalogs';
import store from '@/store';
import parseTrip from 'models/parseTrip';
import camelizeConverter from 'utils/camelizeConverter';
import { decodePassengers } from 'utils/Reserbus';
import { deriveCategoryType } from 'utils/passengerCategories';
import { receiveSearch, setupTrips, receiveTrips } from '@/actions/search';
import { setDynamicPriceAttributes, setProviders, setTransportState } from '@/actions/providers';
import setConfig from '@/actions/config';
import resolveSelectedExchangeTicket from 'utils/exchangeSelectedTicket';
import OpenTicketWrapper from '../../search/OpenTicketWrapper';
import OpenTicketButton from '../../search/OpenTicketButton';
import LabelLoading from '../../../ui/molecules/LabelLoading';
import { filterTrips } from '../../../utils/tripFilters';
import { pickSeenPrice } from '../../../utils/trips/cardSeenPrice';

/**
 * Default filters for TripItem when no filters are applied.
 * Used in exchange flow with SearchWidget to prevent errors when accessing filter properties.
 */
const DEFAULT_FILTERS_APPLIED = {
  departureTime: ['none'],
  categories: [],
  tripOptions: [],
  reclineOptions: [],
  stops: ['none'],
};

/**
 * The seen price an exchange pick sends: the trip's one-way `pricing.total`. An exchange never
 * enters the final arm, and it reads no card context, so BOOK-237's painted figure never applies.
 *
 * @param {object} trip - The picked trip, as a plain object.
 * @returns {number|string|undefined} The seen price.
 */
export const exchangeSeenPrice = (trip) => pickSeenPrice(undefined, trip);

/**
 * Get additional props for SearchWidget in exchange flow
 *
 * `CAN_EXCHANGE_CHANGE_PLACES` decides whether the tenant may move the exchange off its original
 * route. Freezing the fields does not close that door on its own: the widget's swap control
 * rewrites origin and destination without touching either field, so a tenant that forbids the
 * change could still be walked around it. `data-prop-hide-swap-routes-button` drops the control
 * from the DOM entirely, leaving nothing to click.
 *
 * Hiding it is scoped to `USE_NEW_EXCHANGE_UI` on purpose. The gap is just as real for the tenants
 * already on `USE_SEARCH_WIDGET_ON_EXCHANGE`, but closing it for them is a behaviour change they
 * have not opted into; it travels with the redesign instead.
 *
 * `data-prop-opt-in-return` stays `'true'` — its committed value. It does NOT remove the optional
 * return-date field: the widget hides that field only when `optInReturn && !calendarOpen &&
 * tripType === 'oneWay'`, and on the unified date picker, the default today, `optInReturn` never
 * arrives at all. Setting it to `'false'` (as the ticket-transfer branch does) only guarantees the
 * field can never hide.
 *
 * `data-prop-hide-round-trip` is the prop that does remove it, added to the widget for this. It
 * overrides `opt-in-return`, drops the return field and the trip-type switch on both pickers, and
 * discards any preloaded return date, so the search can only ever go out as one way.
 *
 * It is applied only when this exchange has no return leg of its own. The exchange's "return" is
 * a second existing ticket the traveller may also be changing, not a round trip being bought — but
 * a tenant that does carry one still needs the widget able to express it, so the answer comes from
 * what `exchanges/validate` returned for this ticket, never from a flag. With no return leg there
 * is nothing the field could search for, and offering it only invites a search the exchange cannot
 * fulfil.
 *
 * Like the swap control, it travels with `USE_NEW_EXCHANGE_UI`: tenants already on
 * `USE_SEARCH_WIDGET_ON_EXCHANGE` keep the widget they have today.
 * @param {Object} features - Whitelabel features object
 * @param {Object} [options] - Facts about this exchange, as the backend described it.
 * @param {boolean} [options.hasReturnTrip] - Whether the validated exchange carries a return leg.
 *   Defaults to `true`: unknown is treated as "there may be one", so an uninformed caller never
 *   takes an affordance away.
 * @returns {Object} - Additional data-prop-* attributes for SearchWidget
 */
export const getSearchWidgetExchangeProps = (features, { hasReturnTrip = true } = {}) => ({
  'data-prop-freeze-initial': String(!features.CAN_EXCHANGE_CHANGE_PLACES),
  'data-prop-hide-swap-routes-button': String(
    Boolean(features.USE_NEW_EXCHANGE_UI) && !features.CAN_EXCHANGE_CHANGE_PLACES,
  ),
  'data-prop-hide-round-trip': String(Boolean(features.USE_NEW_EXCHANGE_UI) && !hasReturnTrip),
  'data-prop-opt-in-return': 'true',
  'data-prop-allow-hybrid-trip': 'false',
  'data-prop-hybrid-trip': 'false',
  'data-prop-show-trip-switch': 'false',
  'data-prop-growthbook-features': '',
  'data-prop-departure-date-picker-text': '',
  'data-prop-passengers-dropdown': 'false',
});

/**
 * Parse search URL from SearchWidget into search params object
 * @param {string} absoluteSearchUrl - Full URL from SearchWidget event
 * @returns {Object|null} - Parsed search parameters or null if parsing fails
 */
export const parseSearchUrl = (absoluteSearchUrl) => {
  if (!absoluteSearchUrl || typeof absoluteSearchUrl !== 'string') {
    return null;
  }

  try {
    const url = new URL(absoluteSearchUrl);
    const { pathname, search: searchParams } = url;
    const fullUrl = pathname + searchParams;
    const pathParts = pathname.split('/');
    const isRound = pathParts.length === 9;

    if (isRound) {
      const [, , originSlug, destinationSlug, departsDate, returnsDate, , passengers] = pathParts;
      return {
        way: 'departure',
        origin: originSlug,
        destination: destinationSlug,
        date: departsDate,
        returnDate: returnsDate,
        passengers,
        roundTrip: true,
        fullUrl,
      };
    }

    const [, , originSlug, destinationSlug, date, , passengers] = pathParts;
    return {
      way: 'departure',
      origin: originSlug,
      destination: destinationSlug,
      date,
      passengers,
      roundTrip: false,
      fullUrl,
    };
  } catch {
    return null;
  }
};

/**
 * ExchangeTripList component.
 * Displays trip list for exchange with search widget functionality.
 *
 * The heading and the footer are content the panel hosts, not content it hardcodes: both arrive as
 * props and fall back to a default, so a tenant with a different disclaimer set needs no branch in
 * here. The footer renders under USE_NEW_EXCHANGE_UI and carries what the search widget suppresses
 * today — the bottom disclaimer text and the origin/destination notice.
 */
const ExchangeTripList = ({
  originSlug,
  destinationSlug,
  date,
  onSelectTrip,
  newPurchase,
  operationNumbers,
  nit,
  document,
  email,
  transporter,
  canSearch = true,
  openTicket: isOpenTicket,
  setIsLoading,
  paymentPlans,
  installmentsMinAmount,
  tripFilters,
  walletOriginalType,
  parentHasFlatfare,
  // Original ticket info
  originalTrip,
  selectedPassenger,
  // Exchange passenger data for seat selection limits
  exchangePassengers,
  // Seats state from Redux
  seatsBus,
  heading,
  footer,
}) => {
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  // Whether this exchange has a return leg, straight from what `exchanges/validate` answered.
  // Both slices are read, and any one of them is enough: `allPassengers.return` is left empty when
  // the return arrives as connections, and `tripReturn`/`tripReturnConnection` cover that case.
  // Existing slices, not objects built here, so no re-render is added.
  const exchangeTrips = useSelector((state) => state.exchange.trips);
  const returnPassengers = useSelector((state) => state.exchange.allPassengers?.return);
  const exchangeAllPassengers = useSelector((state) => state.exchange.allPassengers);
  const selectedPassengerToAlter = useSelector((state) => state.exchange.selectedPassengerToAlter);
  const { t: tExchange } = useTranslation('exchange');
  const { t: tSearch } = useTranslation('search');
  const { t: tSeats } = useTranslation('seats');
  const history = useHistory();

  const useNewExchangeUi = Boolean(features.USE_NEW_EXCHANGE_UI);
  // The redesigned UI replaces the legacy typeahead + date-picker block with the search widget, the
  // same one `USE_SEARCH_WIDGET_ON_EXCHANGE` already gives its tenants. What the widget lets the
  // user change is not decided here: `CAN_EXCHANGE_CHANGE_PLACES` still freezes origin and
  // destination through `getSearchWidgetExchangeProps`, so no tenant's search rules move.
  const showSearchWidget = Boolean(features.USE_SEARCH_WIDGET_ON_EXCHANGE) || useNewExchangeUi;
  // The panel owns the screen heading whenever it takes over the search controls, since
  // `ExchangeSecondStep` stops rendering its `SectionTitle` — except under the redesign, which
  // wants neither: the ticket summary above the panel already says which tickets are being
  // changed, `labels.select_schedule` titles the list below the widget, and the back control is
  // a plain `history.goBack()`, which is not reliably the ticket list it names. A caller can
  // still supply its own `heading`.
  const showDefaultHeading = showSearchWidget && !useNewExchangeUi;

  const [isPolling, setIsPolling] = useState(false);
  const [searchDetails, setSearchDetails] = useState(undefined);
  const [tripList, setTripList] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // Track the date selected in SearchWidget
  const searchId = useRef(null);
  const hasSearchedBefore = useRef(false);
  const dispatch = useDispatch();
  const { origin: originSlugURL, destination: destinationSlugURL } = useParams();

  /**
   * Get a valid search date. If the provided date is in the past, too soon,
   * or more than 1 year in the future, returns today's date instead.
   * @param {string} dateString - Date string in DD-MM-YYYY format
   * @returns {moment.Moment} Valid moment object for search
   */
  const getValidSearchDate = (dateString) => {
    if (!dateString) return moment();

    const parsedDate = moment(dateString, 'DD-MM-YYYY');
    const today = moment().startOf('day');
    const oneYearFromNow = moment().add(1, 'year').startOf('day');

    // If date is invalid, in the past, or more than 1 year in the future, use today
    const isValidDate =
      parsedDate.isValid() &&
      parsedDate.isSameOrAfter(today) &&
      parsedDate.isBefore(oneYearFromNow);

    return isValidDate ? parsedDate : today;
  };

  /**
   * Format date from DD-MM-YYYY to SearchWidget format (DD-MMM-YY or configured format)
   * If the date is invalid, in the past, or more than 1 year in the future, uses today's date instead.
   * @param {string} dateString - Date string in DD-MM-YYYY format
   * @returns {string|null} Formatted date for SearchWidget
   */
  const formatDateForWidget = (dateString) => {
    if (!dateString) return null;
    const validDate = getValidSearchDate(dateString);
    const searchWidgetDateFormat = env?.searchWidget?.dateFormat;
    return validDate.format(searchWidgetDateFormat?.toUpperCase() || 'DD-MMM-YY');
  };

  /**
   * Process the search polling to get the trips from the search
   * @param {*} response - search response
   */
  const onReceiveBuses = useCallback(
    (response) => {
      const { status, payload } = response;

      if (!payload || status === 'aborted') {
        setIsLoading(false);
        return;
      }

      const currentSearchId = searchId.current;
      if (currentSearchId && payload.id !== currentSearchId) {
        return;
      }

      setIsLoading(false);
      setIsPolling(true);

      const searchResult = camelizeKeys(payload, camelizeConverter);
      const { terminals, lines } = searchResult;
      const { cities } = store.getState();
      const { trips, state } = searchResult;
      const tripCount = searchResult.trips?.length || 0;

      const tripsFiltered = trips.filter((trip) => !trip.redirectTo && trip.isBuyable);
      tripsFiltered.forEach((trip) => parseTrip({ trip, cities, terminals, lines }));

      dispatch(setTerminals(terminals));
      dispatch(setLines(lines));
      dispatch(setProviders(searchResult.id, tripsFiltered, false));

      setSearchDetails(searchResult);

      /**
       * If needed to filter the trips that supports costapass,
       * this conditon has to be added in the next if: && walletOriginalType !== 'costapass'
       */
      if (tripsFiltered.length) {
        setTripList(tripsFiltered);
        dispatch(receiveTrips(RECEIVE_BUSES, searchResult));
      }

      if (state === 'finished' || status === 'retries_exceeded' || status === 'finished') {
        setTripList(tripsFiltered);
        searchResult.state = 'finished';
        dispatch(receiveTrips(RECEIVE_BUSES, searchResult));
        setIsPolling(false);
        if (status === 'retries_exceeded') throw new Error('Polling retries exceeded');
      } else {
        dispatch(setTransportState('bus', searchResult.id, searchResult.state, tripCount));
      }
    },
    [dispatch, setIsLoading],
  );

  /**
   * Handle search from SearchWidget - only updates date since origin and destination are fixed
   * @param {Object} searchParams - Search parameters from SearchWidget
   * @param {string} searchParams.date - Date in format DD-MM-YYYY or DD-MMM-YY
   */
  const handleSearch = useCallback(
    (searchParams) => {
      const {
        date: dateFromWidget,
        origin: originFromWidget,
        destination: destinationFromWidget,
        passengers: passengersFromWidget,
      } = searchParams;

      // Guards every entry point, not just the auto-search effect: the SearchWidget's own
      // search calls this directly, and while the chosen line is unconfirmed a search comes
      // back with every line's trips and no error to catch.
      if (!canSearch) return;

      if (!dateFromWidget) return;

      // Try to parse date in multiple formats:
      // 1. DD-MM-YYYY (used by auto-search from useEffect when date comes from backend)
      // 2. DD-MMM-YY (SearchWidget legacy format)
      // 3. YYYY-MM-DD (SearchWidget new format)
      let dateMoment = moment(dateFromWidget, 'DD-MM-YYYY', true);

      if (!dateMoment.isValid()) {
        dateMoment = getMomentFromSearchDateParam(dateFromWidget);
      }

      if (!dateMoment.isValid()) {
        dateMoment = moment();
      }

      // Validate date is not in the past or too far in the future
      const today = moment().startOf('day');
      const oneYearFromNow = moment().add(1, 'year').startOf('day');

      const validDate =
        dateMoment.isValid() &&
        dateMoment.isSameOrAfter(today) &&
        dateMoment.isBefore(oneYearFromNow)
          ? dateMoment
          : today;
      const formattedDate = validDate.format('DD-MM-YYYY');

      if (showSearchWidget) {
        setSelectedDate(formattedDate);
      }

      setTripList([]);
      setSearchDetails(undefined);
      if (searchId.current) {
        searchApi.stopSearch(searchId.current);
        searchId.current = null;
      }

      const categoryType = passengersFromWidget
        ? deriveCategoryType(decodePassengers(passengersFromWidget))
        : '';

      const payload = {
        date: formattedDate,
        origin: originFromWidget || originSlugURL || originSlug,
        destination: destinationFromWidget || destinationSlugURL || destinationSlug,
        passengers: ['adult'],
        way: 'departure',
        round: false,
      };
      if (categoryType) payload.category_type = categoryType;
      // Task 8.7 (openspec/changes/iamsa-b2c-ticket-exchange, platform-sre, D5): scopes the
      // search to the chosen transporter — `Api::V2::SearchParams` already accepts this
      // (platform-purchase, tarea 6.9) and silently ignores it if it doesn't resolve. Once the
      // search itself is scoped, the open-ticket option (8.8) and the no-results state (8.9)
      // are correct by construction — every trip `ExchangeTripList` ever sees already belongs
      // to `transporter`, so neither needs a separate client-side check.
      if (transporter) payload.transporter = transporter;

      const createSearchParams = {
        onReceiveBuses,
        initFlights: false,
        initRides: false,
      };

      setIsPolling(true);
      // Only show full-page loader on initial search, not on subsequent searches
      // (e.g., when user changes date). For subsequent searches, only LabelLoading is shown.
      if (!hasSearchedBefore.current) {
        setIsLoading(true);
        hasSearchedBefore.current = true;
      }

      searchApi.create(payload, createSearchParams).then((response) => {
        const { cities, lines, config, search } = response;
        const { id } = search;
        const { bus } = search.type_of_transport;
        dispatch(setDynamicPriceAttributes(search));
        dispatch(setConfig(config));
        dispatch(setCities(cities));
        dispatch(receiveSearch('departure', search, lines));
        dispatch(setupTrips(search));
        dispatch(setTransportState('bus', id, bus.active ? 'pending' : 'disabled'));
        setSearchDetails(search);
        searchId.current = id;
      });
    },
    [
      canSearch,
      dispatch,
      originSlug,
      destinationSlug,
      originSlugURL,
      destinationSlugURL,
      setIsLoading,
      onReceiveBuses,
      showSearchWidget,
      transporter,
    ],
  );

  /**
   * Auto-trigger search when all parameters become available
   * Works for both traditional exchange flow and new search widget flow
   */
  useEffect(() => {
    const hasOrigin = originSlug || originSlugURL;
    const hasDestination = destinationSlug || destinationSlugURL;

    if (hasOrigin && hasDestination && date) {
      handleSearch({ date });
    }
  }, [date, originSlug, destinationSlug, originSlugURL, destinationSlugURL, handleSearch]);

  /**
   * Handle search event from SearchWidget by parsing URL and calling handleSearch
   * @param {string} absoluteSearchUrl - Full URL from SearchWidget event
   */
  const handleSearchFromWidget = (absoluteSearchUrl) => {
    const searchParams = parseSearchUrl(absoluteSearchUrl);
    if (!searchParams) return;
    handleSearch(searchParams);
  };

  /**
   * Handle the selection of a trip.
   *
   * @param {string} tripId - The trip identifier.
   * @param {number} seenPrice - The price of the trip seen by the user.
   *
   * @fires newPurchase - Makes a new purchase with the given parameters.
   * @fires onSelectTrip - Triggers the selection of a trip.
   */
  const handleTripSelect = (tripId, seenPrice) => {
    // Get selected seats from Redux state if they exist (from seats on results flow)
    const tripSeatsState = seatsBus?.get?.(tripId);
    const selectedSeatsFromState = tripSeatsState?.get?.('selectedSeats');

    // Format selected seats for the API if they exist
    // IMPORTANT: Must be wrapped in 'preSelectedSeats' to match the format used in normal flow (getSeatsPurchasePayload)
    // IMPORTANT: Preserve ALL seat fields needed for locking (seatLevel, seatFloor, seatFareId, seatFareDescription)
    let preSelectedSeats = null;
    if (selectedSeatsFromState && selectedSeatsFromState.length > 0) {
      preSelectedSeats = {
        preSelectedSeats: {
          departure: selectedSeatsFromState.map((seat) => ({
            number: seat.number,
            seatLevel: seat.seatLevel || seat.level,
            // Include all optional fields that might be needed for seat locking
            ...(seat.seatFloor && { seatFloor: seat.seatFloor }),
            ...(seat.seatFareId && { seatFareId: seat.seatFareId }),
            ...(seat.seatFareDescription && { seatFareDescription: seat.seatFareDescription }),
          })),
        },
      };
    }

    newPurchase(
      tripId,
      seenPrice,
      { operationNumbers, nit, document, email, transporter },
      preSelectedSeats,
    );
    onSelectTrip(true);
  };

  const tripsToRender = tripList.filter(
    (trip) => trip.openTicket === false && trip.redirectTo === null,
  );

  const showNoResults = searchDetails && isPolling === false && tripsToRender.length === 0;

  /**
   * Render the no results message when search is complete but no trips were found.
   */
  const renderNoResults = () => {
    const dateMoment = moment(date, 'DD-MM-YYYY');
    const formattedDate = tSearch('date_human_sentence', {
      weekDay: dateMoment.format('dddd'),
      day: dateMoment.format('D'),
      month: dateMoment.format('MMMM'),
      year: dateMoment.year(),
    });
    const notTripsMessage =
      walletOriginalType !== 'travelpass' && walletOriginalType !== 'costapass'
        ? `${tSearch('schedules_not_found')}: ${formattedDate}`
        : tSearch('schedules_not_found_travelpass');

    return (
      <div className="no-results-wrapper mt-6">
        <Spacing size="XL" vertical alignItems="flex-start">
          <Spacing size="XL" vertical>
            <Spacing alignItems="center">
              <Icon type="Calendar" size="L" />
              <Text size="L" weight="bold">
                {notTripsMessage}
              </Text>
            </Spacing>
          </Spacing>
        </Spacing>
      </div>
    );
  };

  const immutableTrips = List(tripsToRender.map((trip) => Map(trip)));
  const tripsFiltered = filterTrips(immutableTrips, null, '', tripFilters.get('filters'));
  const openTrip = tripList.find((trip) => trip.openTicket === true && trip.redirectTo === null);
  const showTrips = searchDetails && tripsToRender.length > 0;

  // FLATFARE BUSINESS RULE: Hide open ticket option when original purchase was made with flatfare
  // This prevents flatfare customers from using their single exchange on an open ticket
  const showOpenTicket =
    !isOpenTicket &&
    features.CAN_EXCHANGE_OPEN_TICKET &&
    openTrip &&
    showTrips &&
    !parentHasFlatfare;

  /**
   * Select open trip list and redirect to search page
   */
  const selectOpenTripList = () => {
    const origin = openTrip?.originId;
    const destination = openTrip?.destinationId;
    const departureDate = getSearchDateParamFromMoment(moment(searchDetails?.departs));
    const route = `/search/${origin}/${destination}/${departureDate}/p/A1/departures/open?isExchange=true`;
    history.push(route);
  };

  /**
   * Handle open trip selection
   */
  const handleOnOpenTripSelected = () => {
    return features.OPEN_TICKET_PROVIDER_SELECTION_ENABLED
      ? selectOpenTripList()
      : handleTripSelect(openTrip.id, exchangeSeenPrice(openTrip));
  };

  /**
   * Render the original ticket card being exchanged.
   * Uses the same visual structure and properties as TicketsListBlock and TicketList components.
   */
  const renderOriginalTicketCard = () => {
    // The redesigned presentation renders these same tickets above the search controls; rendering
    // both would show each ticket twice.
    if (useNewExchangeUi) return null;
    if (!originalTrip || !features.USE_SEARCH_WIDGET_ON_EXCHANGE || !selectedPassenger) return null;

    const {
      firstName = '',
      lastName = '',
      secondLastName = '',
      seatNumber,
      seatLevel,
      category,
      transporterKey,
      serviceTypeProvider,
      statusProvider,
    } = selectedPassenger;

    const showEnhancedTicketData = features.SHOW_ENHANCED_TICKET_DATA;

    // Format date like TicketsListBlock: "Lunes, 15 Enero"
    const formattedDateTime = originalTrip.date
      ? moment(originalTrip.date, 'DD-MM-YYYY')
          .format('dddd, D MMMM')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : '';

    // Format time: 24h when tenant has HOUR_FORMAT '24' (e.g. CDS), else 12h
    const timeFormat = features.HOUR_FORMAT === '24' ? 'HH:mm' : 'hh:mm A';
    const formattedTime = originalTrip.time
      ? moment(originalTrip.time, 'HH:mm').format(timeFormat)
      : '';

    /**
     * Renders ticket details matching TicketList component logic
     */
    const renderTicketDetails = () => {
      const items = [
        {
          key: 'category',
          label: tExchange('ticket_list.category'),
          value: tSeats(`passengers:type.${category}`),
          condition: !showEnhancedTicketData && category,
        },
        {
          key: 'ticket-number',
          label: tExchange('ticket_list.ticket_number'),
          value: transporterKey,
          condition: showEnhancedTicketData && transporterKey,
        },
        {
          key: 'seat',
          label: tExchange('ticket_list.seat'),
          value: seatNumber,
          condition: !!seatNumber,
        },
        {
          key: 'seat-level',
          label: tExchange('ticket_list.level'),
          value: seatLevel,
          condition: showEnhancedTicketData && seatLevel,
        },
        {
          key: 'service-type-provider',
          label: tExchange('ticket_list.service_type'),
          value: serviceTypeProvider,
          condition: showEnhancedTicketData && serviceTypeProvider,
        },
      ];

      return (
        <div className="inline-grid grid-cols-2 gap-y-2 gap-x-6 w-fit">
          {items
            .filter(({ condition }) => condition)
            .map(({ key, label, value }) => (
              <Text key={key} size="S">
                {label + (label && ':')} {value}
              </Text>
            ))}
        </div>
      );
    };

    return (
      <div className="mb-4">
        <Text size="S" color="grayMedium" weight="semibold" elementType="p" className="mb-2">
          {tExchange('labels.ticket_being_exchanged')}
        </Text>
        <div className="w-full">
          <p className="mb-2 text-sm">
            <span>
              {originalTrip.originDisplay} - {originalTrip.destinationDisplay}
            </span>
          </p>
          <div className="border-b border-2 rounded-md p-5">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <Icon type="CalendarOutlined" size="M" />
                <span className="font-medium text-sm">
                  {formattedTime && <strong>{formattedTime}</strong>}
                  {formattedTime && formattedDateTime && ', '}
                  {formattedDateTime}
                  {' - '}1 {tExchange('ticket_list.tickets')}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-4">
              <TicketBox className="relative">
                {statusProvider && statusProvider.trim() !== '' && (
                  <div className="absolute top-2 right-3 z-10">
                    <Badge type="success" size="small" roundCorners smallPadding>
                      <Text size="S" weight="semibold" color="white">
                        {statusProvider}
                      </Text>
                    </Badge>
                  </div>
                )}
                <Spacing alignItems="center">
                  <Spacing size="XS" vertical>
                    <Text weight="semibold">
                      {firstName} {lastName} {secondLastName}
                    </Text>
                    <Spacing>{renderTicketDetails()}</Spacing>
                  </Spacing>
                </Spacing>
              </TicketBox>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Panel heading: the schedule title and the control that returns to ticket selection.
   * @returns {JSX.Element}
   */
  const renderDefaultHeading = () => (
    <>
      <div className="mb-4">
        <Button
          onClick={() => history.goBack()}
          text={`${tExchange('button.back_to_tickets')}`}
          variant="noFill"
          className="!p-0"
          buttonType="button"
        />
      </div>
      <h1 className="home-title mb-6" data-testid="interchange-schedule-title">
        {tExchange('labels.trip_schedule')}
      </h1>
    </>
  );

  /**
   * Panel footer: the notice that origin and destination cannot be changed, and nothing else.
   *
   * Whether the notice appears is the tenant's call, through `SHOW_EXCHANGE_PLACES_NOTICE`. It
   * used to be nobody's: the notice only ever rendered on the legacy typeahead path, so adopting
   * `USE_SEARCH_WIDGET_ON_EXCHANGE` silently took it away. That was survivable while the widget
   * was opt-in; the redesign turns it on for everyone, so the same side effect would have decided
   * this for every tenant at once — and, read the other way, would have put IAMSA's notice on a
   * brand that has never carried it. The flag replaces the side effect with an answer.
   *
   * A tenant that has not answered keeps exactly what it shows today, which is what the fallback
   * reproduces. `CAN_EXCHANGE_CHANGE_PLACES` still has the last word either way: a tenant that
   * may move the exchange off its route has nothing to disclaim.
   *
   * `ticket_list.bottom_text` is deliberately absent. It states commercial terms — three hours,
   * a five percent penalty — that no flag qualifies, and it belongs to `/exchange/ticket-list`,
   * where it still renders. This screen never carried it, and relocating it would hand every
   * tenant a policy it may not have.
   * @returns {JSX.Element|null}
   */
  const renderDefaultFooter = () => {
    if (features.CAN_EXCHANGE_CHANGE_PLACES) return null;

    const showsPlacesNotice =
      features.SHOW_EXCHANGE_PLACES_NOTICE ?? !features.USE_SEARCH_WIDGET_ON_EXCHANGE;
    if (!showsPlacesNotice) return null;

    return (
      <div className="mt-8 flex flex-col gap-3" data-testid="interchange-schedule-footer">
        <Alert alertType="info" title={tExchange('info.change_places')} />
      </div>
    );
  };

  const widgetOrigin = originSlugURL || originSlug;
  const widgetDestination = destinationSlugURL || destinationSlug;
  const widgetDepartureDate = formatDateForWidget(selectedDate || date);
  // The widget is a web component: it reads its `data-prop-*` values once, when it boots, and
  // ignores whatever arrives afterwards. Under `USE_NEW_EXCHANGE_UI` the routes stay mounted while
  // `validateExchange` resolves, so this panel renders before the trip is known — booting the
  // widget then would leave its place and date fields permanently empty. Waiting costs nothing on
  // the legacy path, where the loader replaces the routes and these values are already in by the
  // time the panel mounts.
  const widgetReady = Boolean(widgetOrigin && widgetDestination && widgetDepartureDate);
  // Read at the same moment the widget boots, which is safe: `widgetReady` waits on the trip date,
  // and the exchange reducer writes it in the very action that fills these slices. The places may
  // come from the URL before that, the date cannot — so by the time the widget takes its props
  // once and for all, this answer is the backend's and not a default.
  // Whether this exchange has a return leg of its own to express — not whether the purchase has
  // one. A traveller who picked a single ticket is rescheduling that one leg, so offering a return
  // date asks for a trip the exchange cannot make; a tenant whose flow changes the whole purchase
  // at once still needs it. `isPicked` is the same answer the summary uses to decide which ticket
  // it shows.
  const { isPicked: isSingleTicketExchange } = resolveSelectedExchangeTicket({
    selectedPassengerToAlter,
    allPassengers: exchangeAllPassengers,
    passengers: exchangePassengers,
  });
  const hasReturnTrip =
    !isSingleTicketExchange &&
    Boolean(
      returnPassengers?.length ||
        exchangeTrips?.tripReturnConnection?.length ||
        (exchangeTrips?.tripReturn && Object.keys(exchangeTrips.tripReturn).length),
    );

  return (
    <div id="ticket-interchange-schedule-container" className="p-5 bg-white rounded-xl min-h-96">
      {heading ?? (showDefaultHeading ? renderDefaultHeading() : null)}
      {showSearchWidget && (
        <>
          {renderOriginalTicketCard()}
          <div className="mb-8">
            {widgetReady ? (
              <SearchWidget
                origin={widgetOrigin}
                destination={widgetDestination}
                departureDate={widgetDepartureDate}
                onSearch={handleSearchFromWidget}
                additionalProps={getSearchWidgetExchangeProps(features, { hasReturnTrip })}
              />
            ) : (
              <LabelLoading />
            )}
          </div>
        </>
      )}
      {showNoResults && renderNoResults()}
      {searchDetails && !showNoResults && (
        <div className="mt-6">
          <Spacing vertical size="L">
            <Text weight="semibold" size="M">
              {tExchange('labels.select_schedule')}
            </Text>
            {showTrips &&
              features.SHOW_NON_EXACT_PRICE_MESSAGE &&
              !features.HIDE_PRICE_ON_EXCHANGE && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                  <div className="flex items-center gap-3">
                    <Icon type="Info" size="M" color="primary" className="flex-shrink-0" />
                    <Text size="S" color="grayDark">
                      {tExchange('info.price_disclaimer')}
                    </Text>
                  </div>
                </div>
              )}
            <Spacing vertical size="S">
              {isPolling && <LabelLoading />}
              {showOpenTicket && (
                <Spacing justifyContent="flex-end">
                  <OpenTicketWrapper>
                    <OpenTicketButton
                      trip={openTrip}
                      selectTrip={handleOnOpenTripSelected}
                      isExchange
                    />
                  </OpenTicketWrapper>
                </Spacing>
              )}
              {showTrips &&
                tripsFiltered.toJS().map((trip, index) => {
                  const origin = searchDetails.terminals[trip.originId];
                  const destination = searchDetails.terminals[trip.destinationId];

                  const hasDiscountType = Boolean(trip.pricing.discountType);

                  return (
                    <TripItem
                      key={trip.id}
                      trip={trip}
                      onSelectClick={() => handleTripSelect(trip.id, exchangeSeenPrice(trip), trip)}
                      selectTrip={() => handleTripSelect(trip.id, exchangeSeenPrice(trip), trip)}
                      arrival={trip.arrival}
                      availability={trip.availability}
                      departure={trip.departure}
                      destination={destination}
                      duration={trip.duration}
                      id={trip.id}
                      isRoundTrip={false}
                      origin={origin}
                      providerDiscount={trip.pricing.providerDiscount}
                      providerDiscounts={trip.pricing.providerDiscounts}
                      total={trip.pricing.total}
                      totalBeforeDiscount={trip.pricing.totalBeforeDiscount}
                      way="departure"
                      supportWoman={trip.supportWoman}
                      availableCategories={trip.passengerTypes}
                      hasTransit={trip.hasTransit}
                      stopoverPlace={trip.stopoverPlace}
                      capacity={trip.capacity}
                      type={trip.type || trip.transportType}
                      serviceType={trip.transportType === 'bus' ? trip.line.serviceType : null}
                      hasDiscountType={hasDiscountType}
                      hasRouteDetails={Boolean(trip.hasRouteDetails)}
                      position={index + 1}
                      paymentPlans={paymentPlans}
                      installmentsMinAmount={installmentsMinAmount}
                      variableDepartureTime={trip.variableDepartureTime || false}
                      isExchange
                      exchangePassengers={exchangePassengers}
                      filtersApplied={DEFAULT_FILTERS_APPLIED}
                      pricing={trip.pricing}
                      // The seat map on this list. Everything behind it already exists:
                      // `TripItem` turns `exchangePassengers` into `fromExchange`,
                      // `getSeatSelectorPropsFromTrip` reads its length as `minPassengers`, and
                      // `SeatSelector`'s exchange rule keeps the selection to exactly that many
                      // seats — so the cap is the number of tickets being exchanged, for free.
                      //
                      // The redesign only stops forcing this off; it does not grant it. The trip
                      // still has the last word through its own `allowsSeatSelection`, exactly as
                      // `TripList` passes it on the regular search flow, and the tenant still has
                      // to have `SEATS_ON_RESULTS`/`RESULTS_SEATS` on.
                      allowsSeatSelection={useNewExchangeUi && Boolean(trip.allowsSeatSelection)}
                    />
                  );
                })}
            </Spacing>
          </Spacing>
        </div>
      )}
      {useNewExchangeUi && (footer ?? renderDefaultFooter())}
    </div>
  );
};

ExchangeTripList.propTypes = {
  originSlug: PropTypes.string.isRequired,
  destinationSlug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  onSelectTrip: PropTypes.func.isRequired,
  walletOriginalType: PropTypes.string,
  newPurchase: PropTypes.func.isRequired,
  operationNumbers: PropTypes.array.isRequired,
  nit: PropTypes.string,
  document: PropTypes.string,
  email: PropTypes.string,
  transporter: PropTypes.string,
  canSearch: PropTypes.bool,
  setIsLoading: PropTypes.func.isRequired,
  paymentPlans: PropTypes.any,
  installmentsMinAmount: PropTypes.number,
  openTicket: PropTypes.bool,
  tripFilters: PropTypes.object,
  parentHasFlatfare: PropTypes.bool,
  seatsBus: PropTypes.object,
  exchangePassengers: PropTypes.array,
  /** Overrides the panel heading; defaults to the schedule title plus the back control. */
  heading: PropTypes.node,
  /** Overrides the panel footer; defaults to the disclaimer plus the places notice. */
  footer: PropTypes.node,
  // Original ticket info
  originalTrip: PropTypes.shape({
    originDisplay: PropTypes.string,
    destinationDisplay: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
  }),
  selectedPassenger: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    secondLastName: PropTypes.string,
    transporterKey: PropTypes.string,
    seatNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    seatLevel: PropTypes.string,
    category: PropTypes.string,
    serviceTypeProvider: PropTypes.string,
    statusProvider: PropTypes.string,
  }),
};

export default ExchangeTripList;
